import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Plus, Pencil, Trash, X, User, Shield, ShieldCheck } from '@phosphor-icons/react';
import { useAuthStore } from '../../store';
import { formatDate } from '../../utils/helpers';
import { toast } from 'sonner';
import api, { get2FAStatus, setup2FA, verify2FA, disable2FA, getSessions, revokeOtherSessions } from '../../utils/api';

const AdminUsers = () => {
  const { user: currentUser } = useAuthStore();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    role: 'admin'
  });
  const [submitting, setSubmitting] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  
  // 2FA State
  const [show2FASetup, setShow2FASetup] = useState(false);
  const [twoFactorSecret, setTwoFactorSecret] = useState('');
  const [twoFactorQR, setTwoFactorQR] = useState('');
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);
  const [loading2FAStatus, setLoading2FAStatus] = useState(true);

  // Sessions State
  const [sessions, setSessions] = useState([]);
  const [loadingSessions, setLoadingSessions] = useState(true);
  const [showSessions, setShowSessions] = useState(false);

  const fetchUsers = async () => {
    try {
      const res = await api.get('/api/admin/users');
      setUsers(res.data.data || []);
    } catch (error) {
      console.error('Error fetching users:', error);
      // If not authorized (not super_admin), show only current user
      if (error.response?.status === 403) {
        setUsers([currentUser]);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
    fetch2FAStatus();
    fetchSessions();
  }, []);

  const fetch2FAStatus = async () => {
    try {
      const res = await get2FAStatus();
      setTwoFactorEnabled(res.data.data.enabled);
    } catch (error) {
      console.error('Error fetching 2FA status:', error);
    } finally {
      setLoading2FAStatus(false);
    }
  };

  const fetchSessions = async () => {
    try {
      const res = await getSessions();
      setSessions(res.data.data.sessions || []);
    } catch (error) {
      console.error('Error fetching sessions:', error);
    } finally {
      setLoadingSessions(false);
    }
  };

  const resetForm = () => {
    setFormData({
      username: '',
      password: '',
      role: 'admin'
    });
    setEditingUser(null);
  };

  const openCreateModal = () => {
    resetForm();
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const userData = {
        username: formData.username,
        password: formData.password,
        role: formData.role
      };

      await api.post('/api/admin/users', userData);
      toast.success('User created successfully');
      setShowModal(false);
      resetForm();
      fetchUsers();
    } catch (error) {
      console.error('Error creating user:', error);
      toast.error(error.response?.data?.detail || 'Failed to create user');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (userId) => {
    if (userId === currentUser?.id) {
      toast.error('You cannot delete your own account');
      return;
    }
    if (!window.confirm('Are you sure you want to delete this user?')) return;
    
    try {
      await api.delete(`/api/admin/users/${userId}`);
      toast.success('User deleted successfully');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (passwordData.new_password !== passwordData.confirm_password) {
      toast.error('New passwords do not match');
      return;
    }

    setSubmitting(true);
    try {
      await api.put('/api/admin/change-password', null, {
        params: {
          current_password: passwordData.current_password,
          new_password: passwordData.new_password
        }
      });
      toast.success('Password changed successfully');
      setShowChangePassword(false);
      setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setSubmitting(false);
    }
  };

  // 2FA Handlers
  const handleSetup2FA = async () => {
    setSubmitting(true);
    try {
      const res = await setup2FA();
      setTwoFactorSecret(res.data.data.secret);
      setTwoFactorQR(res.data.data.qr_code);
      setShow2FASetup(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to setup 2FA');
    } finally {
      setSubmitting(false);
    }
  };

  const handleVerify2FA = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await verify2FA(twoFactorCode);
      toast.success('2FA enabled successfully!');
      setShow2FASetup(false);
      setTwoFactorCode('');
      setTwoFactorSecret('');
      setTwoFactorQR('');
      setTwoFactorEnabled(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid verification code');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDisable2FA = async () => {
    if (!window.confirm('Are you sure you want to disable 2FA?')) return;
    
    setSubmitting(true);
    try {
      await disable2FA();
      toast.success('2FA disabled successfully');
      setTwoFactorEnabled(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to disable 2FA');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRevokeOtherSessions = async () => {
    if (!window.confirm('Are you sure you want to logout from all other devices?')) return;
    
    setSubmitting(true);
    try {
      await revokeOtherSessions();
      toast.success('Logged out from all other devices');
      fetchSessions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to revoke sessions');
    } finally {
      setSubmitting(false);
    }
  };

  const isSuperAdmin = currentUser?.role === 'super_admin';

  return (
    <div className="space-y-6">
      {/* 2FA Section */}
      <div className="bg-white p-6 rounded-lg shadow-sm border border-border-default">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
              <ShieldCheck size={24} className="text-primary" />
            </div>
            <div>
              <h3 className="font-display text-lg text-secondary">Two-Factor Authentication</h3>
              <p className="font-body text-sm text-text-secondary">
                {twoFactorEnabled 
                  ? "Your account is protected with 2FA" 
                  : "Add an extra layer of security to your account"}
              </p>
            </div>
          </div>
          {loading2FAStatus ? (
            <div className="btn-secondary flex items-center gap-2 opacity-50">
              <Shield size={18} />
              Loading...
            </div>
          ) : twoFactorEnabled ? (
            <button
              onClick={handleDisable2FA}
              disabled={submitting}
              className="btn-danger flex items-center gap-2"
            >
              <Shield size={18} />
              Disable 2FA
            </button>
          ) : (
            <button
              onClick={handleSetup2FA}
              disabled={submitting}
              className="btn-secondary flex items-center gap-2"
            >
              <Shield size={18} />
              Enable 2FA
            </button>
          )}
        </div>
      </div>

      {/* Active Sessions Section */}
      <div className="bg-white p-6 rounded-lg shadow-sm border border-border-default">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
            </div>
            <div>
              <h3 className="font-display text-lg text-secondary">Active Sessions</h3>
              <p className="font-body text-sm text-text-secondary">
                Manage your active login sessions
              </p>
            </div>
          </div>
          <button
            onClick={() => setShowSessions(!showSessions)}
            className="btn-secondary flex items-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
            </svg>
            {showSessions ? 'Hide Sessions' : 'View Sessions'}
          </button>
        </div>
        
        {showSessions && (
          <div className="space-y-3">
            {loadingSessions ? (
              <p className="text-text-secondary">Loading sessions...</p>
            ) : sessions.length === 0 ? (
              <p className="text-text-secondary">No active sessions found</p>
            ) : (
              <>
                {sessions.map((session) => (
                  <div 
                    key={session.id} 
                    className={`flex items-center justify-between p-3 rounded-lg ${session.is_current ? 'bg-green-50 border border-green-200' : 'bg-gray-50 border border-gray-200'}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-2 h-2 rounded-full ${session.is_current ? 'bg-green-500' : 'bg-gray-400'}`}></div>
                      <div>
                        <p className="font-medium text-secondary">
                          {session.device_info || 'Unknown Device'}
                          {session.is_current && (
                            <span className="ml-2 text-xs text-green-600 bg-green-100 px-2 py-0.5 rounded">Current</span>
                          )}
                        </p>
                        <p className="text-sm text-text-secondary">
                          IP: {session.ip_address || 'Unknown'} • Logged in: {new Date(session.created_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
                
                {sessions.length > 1 && (
                  <button
                    onClick={handleRevokeOtherSessions}
                    disabled={submitting}
                    className="btn-danger w-full mt-4 flex items-center justify-center gap-2"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                    </svg>
                    Logout from all other devices
                  </button>
                )}
              </>
            )}
          </div>
        )}
      </div>

      {/* 2FA Setup Modal */}
      {show2FASetup && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white w-full max-w-md"
          >
            <div className="flex items-center justify-between p-4 border-b border-border-default">
              <h3 className="font-display text-xl text-secondary">Setup 2FA</h3>
              <button onClick={() => setShow2FASetup(false)}>
                <X size={24} className="text-text-muted hover:text-secondary" />
              </button>
            </div>
            
            <div className="p-6 space-y-4">
              <p className="font-body text-sm text-text-secondary">
                Scan this QR code with your authenticator app (Google Authenticator, Authy, etc.)
              </p>
              
              <div className="flex justify-center">
                <img 
                  src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(twoFactorQR)}`} 
                  alt="2FA QR Code" 
                  className="border rounded-lg"
                />
              </div>
              
              <div className="bg-background-subtle p-4 rounded-lg">
                <p className="font-body text-xs text-text-muted mb-2">Or enter this secret manually:</p>
                <code className="font-mono text-sm break-all">{twoFactorSecret}</code>
              </div>
              
              <form onSubmit={handleVerify2FA} className="space-y-4">
                <div>
                  <label className="block font-body text-sm text-text-secondary mb-2">
                    Enter verification code
                  </label>
                  <input
                    type="text"
                    value={twoFactorCode}
                    onChange={(e) => setTwoFactorCode(e.target.value)}
                    maxLength={6}
                    required
                    className="w-full px-4 py-3 border border-border-default focus:border-primary focus:outline-none font-body text-center text-2xl tracking-widest"
                    placeholder="000000"
                  />
                </div>
                
                <button
                  type="submit"
                  disabled={submitting}
                  className="btn-primary w-full"
                >
                  {submitting ? 'Verifying...' : 'Verify & Enable 2FA'}
                </button>
              </form>
            </div>
          </motion.div>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="font-display text-2xl text-secondary">User Management</h1>
          <p className="font-body text-sm text-text-secondary">
            Logged in as: {currentUser?.username} ({currentUser?.role})
          </p>
        </div>
        
        <div className="flex gap-4">
          <button
            onClick={() => setShowChangePassword(true)}
            data-testid="change-password-btn"
            className="btn-secondary flex items-center gap-2"
          >
            Change Password
          </button>
          {isSuperAdmin && (
            <button
              onClick={openCreateModal}
              data-testid="add-user-btn"
              className="btn-primary flex items-center gap-2"
            >
              <Plus size={18} />
              Add User
            </button>
          )}
        </div>
      </div>

      {/* Current User Profile */}
      <div className="admin-card">
        <h3 className="font-display text-lg text-secondary mb-4">Your Profile</h3>
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center">
            <User size={32} className="text-primary" />
          </div>
          <div>
            <p className="font-display text-xl text-secondary">{currentUser?.username}</p>
            <div className="flex items-center gap-2 mt-1">
              <Shield size={16} className={currentUser?.role === 'super_admin' ? 'text-primary' : 'text-text-muted'} />
              <span className="font-body text-sm text-text-secondary capitalize">{currentUser?.role?.replace('_', ' ')}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Users List (Super Admin Only) */}
      {isSuperAdmin && (
        <div className="admin-card">
          <h3 className="font-display text-lg text-secondary mb-4">All Users</h3>
          
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border-default bg-background-subtle">
                  <th className="text-left font-body text-xs uppercase tracking-wider text-text-secondary py-4 px-4">User</th>
                  <th className="text-left font-body text-xs uppercase tracking-wider text-text-secondary py-4 px-4">Role</th>
                  <th className="text-left font-body text-xs uppercase tracking-wider text-text-secondary py-4 px-4">Created</th>
                  <th className="text-right font-body text-xs uppercase tracking-wider text-text-secondary py-4 px-4">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  [...Array(3)].map((_, i) => (
                    <tr key={i} className="border-b border-border-default">
                      <td colSpan={4} className="py-4 px-4">
                        <div className="h-12 bg-gray-100 animate-pulse rounded" />
                      </td>
                    </tr>
                  ))
                ) : users.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="py-12 text-center">
                      <p className="font-body text-text-secondary">No users found</p>
                    </td>
                  </tr>
                ) : (
                  users.map((user) => (
                    <motion.tr
                      key={user.id}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="border-b border-border-default hover:bg-background-subtle transition-colors"
                      data-testid={`user-row-${user.id}`}
                    >
                      <td className="py-4 px-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                            <span className="font-display text-primary">
                              {user.username?.charAt(0).toUpperCase()}
                            </span>
                          </div>
                          <div>
                            <p className="font-body text-sm text-secondary font-medium">{user.username}</p>
                            {user.id === currentUser?.id && (
                              <span className="font-body text-xs text-primary">(You)</span>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="py-4 px-4">
                        <span className={`font-body text-xs px-2 py-1 rounded capitalize ${
                          user.role === 'super_admin' 
                            ? 'bg-primary/10 text-primary' 
                            : 'bg-gray-100 text-gray-700'
                        }`}>
                          {user.role?.replace('_', ' ')}
                        </span>
                      </td>
                      <td className="py-4 px-4">
                        <span className="font-body text-sm text-text-secondary">
                          {formatDate(user.created_at)}
                        </span>
                      </td>
                      <td className="py-4 px-4">
                        <div className="flex items-center justify-end gap-2">
                          {user.id !== currentUser?.id && (
                            <button
                              onClick={() => handleDelete(user.id)}
                              data-testid={`delete-user-${user.id}`}
                              className="p-2 text-text-muted hover:text-red-500 transition-colors"
                              title="Delete"
                            >
                              <Trash size={18} />
                            </button>
                          )}
                        </div>
                      </td>
                    </motion.tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Create User Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white w-full max-w-md"
          >
            <div className="flex items-center justify-between p-4 border-b border-border-default">
              <h3 className="font-display text-xl text-secondary">Add New User</h3>
              <button onClick={() => { setShowModal(false); resetForm(); }}>
                <X size={24} className="text-text-muted hover:text-secondary" />
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block font-body text-sm text-text-secondary mb-2">Username *</label>
                <input
                  type="text"
                  value={formData.username}
                  onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                  required
                  minLength={3}
                  data-testid="user-username"
                  className="w-full px-4 py-3 border border-border-default focus:border-primary focus:outline-none font-body"
                  placeholder="Enter username"
                />
              </div>

              <div>
                <label className="block font-body text-sm text-text-secondary mb-2">Password *</label>
                <input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required
                  minLength={6}
                  data-testid="user-password"
                  className="w-full px-4 py-3 border border-border-default focus:border-primary focus:outline-none font-body"
                  placeholder="Enter password (min 6 characters)"
                />
              </div>

              <div>
                <label className="block font-body text-sm text-text-secondary mb-2">Role *</label>
                <select
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                  required
                  data-testid="user-role"
                  className="w-full px-4 py-3 border border-border-default focus:border-primary focus:outline-none font-body bg-white"
                >
                  <option value="admin">Admin</option>
                  <option value="super_admin">Super Admin</option>
                </select>
              </div>
              
              <div className="flex gap-4 pt-4">
                <button 
                  type="button" 
                  onClick={() => { setShowModal(false); resetForm(); }} 
                  className="btn-secondary flex-1"
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  disabled={submitting}
                  data-testid="save-user-btn" 
                  className="btn-primary flex-1 disabled:opacity-50"
                >
                  {submitting ? 'Creating...' : 'Create User'}
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}

      {/* Change Password Modal */}
      {showChangePassword && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white w-full max-w-md"
          >
            <div className="flex items-center justify-between p-4 border-b border-border-default">
              <h3 className="font-display text-xl text-secondary">Change Password</h3>
              <button onClick={() => setShowChangePassword(false)}>
                <X size={24} className="text-text-muted hover:text-secondary" />
              </button>
            </div>
            
            <form onSubmit={handleChangePassword} className="p-6 space-y-4">
              <div>
                <label className="block font-body text-sm text-text-secondary mb-2">Current Password *</label>
                <input
                  type="password"
                  value={passwordData.current_password}
                  onChange={(e) => setPasswordData({ ...passwordData, current_password: e.target.value })}
                  required
                  data-testid="current-password"
                  className="w-full px-4 py-3 border border-border-default focus:border-primary focus:outline-none font-body"
                  placeholder="Enter current password"
                />
              </div>

              <div>
                <label className="block font-body text-sm text-text-secondary mb-2">New Password *</label>
                <input
                  type="password"
                  value={passwordData.new_password}
                  onChange={(e) => setPasswordData({ ...passwordData, new_password: e.target.value })}
                  required
                  minLength={6}
                  data-testid="new-password"
                  className="w-full px-4 py-3 border border-border-default focus:border-primary focus:outline-none font-body"
                  placeholder="Enter new password"
                />
              </div>

              <div>
                <label className="block font-body text-sm text-text-secondary mb-2">Confirm New Password *</label>
                <input
                  type="password"
                  value={passwordData.confirm_password}
                  onChange={(e) => setPasswordData({ ...passwordData, confirm_password: e.target.value })}
                  required
                  minLength={6}
                  data-testid="confirm-password"
                  className="w-full px-4 py-3 border border-border-default focus:border-primary focus:outline-none font-body"
                  placeholder="Confirm new password"
                />
              </div>
              
              <div className="flex gap-4 pt-4">
                <button 
                  type="button" 
                  onClick={() => setShowChangePassword(false)} 
                  className="btn-secondary flex-1"
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  disabled={submitting}
                  data-testid="save-password-btn" 
                  className="btn-primary flex-1 disabled:opacity-50"
                >
                  {submitting ? 'Changing...' : 'Change Password'}
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default AdminUsers;
